import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChatPage from './pages/ChatPage';
import SongsPage from './pages/SongsPage';
import PlayerPage from './pages/PlayerPage';
import MePage from './pages/MePage';
import SettingsPage from './pages/SettingsPage';
import SharePage from './pages/SharePage';
/** ⭐ 0017（`decisions/041`）：登入（Email Magic Link） */
import LoginPage from './pages/LoginPage';
import { useAuth } from './hooks/useAuth';
import { getDeviceId, openSessionId, startNewSession } from './hooks/useChatStream';
import { adoptDeviceFromUrl } from './deviceRecovery';
import TopBar from './components/TopBar';
import Drawer, { type DrawerSession } from './components/Drawer';
import MiniBar from './player/MiniBar';
import { usePlayer } from './player/PlayerContext';
import { Icons } from './components/Icons';

/**
 * ⭐ **底部 tab bar（3 格）** —— `decisions/053`（2026-10-11；**推翻** UI v2「取消底部 tab bar」）：
 *   產品負責人實測之後要求返：**傾偈／我的情緒日記／我的播放清單**；
 *   ⋯ 抽屜**保留**（設定／帳號／語言／新對話／之前嘅對話）。
 */
const TABS = [
  { to: '/chat', key: 'tab_chat', Icon: Icons.Chat },
  { to: '/songs', key: 'tab_diary', Icon: Icons.Diary },
  { to: '/player', key: 'tab_playlists', Icon: Icons.Playlist },
] as const;

/**
 * CoSingery — App shell（路由 ＋ 頂欄 ＋ **右側抽屜**）
 *
 * ⭐ **2026-10-11 UI v2 步 4**（`decisions/052`／`tasks/J-ui-cosingery`）：
 *   · **取消底部 tab bar** → 所有導航收入右上 **⋯** → 右側全高抽屜（設計 C13；`Drawer.tsx`）
 *   · 頂欄改用設計 **C02**（`TopBar.tsx`；品牌 ＋ ⋯）
 *   · 常駐播放器 mini bar ＝ **步 8**（設計 C10）—— 在此之前用抽屜「播放清單」暫時到 `/player`
 *
 * ⚠️ `--app-vh`：手機可視區真實高度（`visualViewport`）—— shell 用佢做高度，避免「整頁捲」。
 * ⚠️ 文字一律 i18n；顏色一律 CSS var。
 */

interface SessionRow {
  sessionId: string;
  title?: string | null;
  preview: string;
  songs: number;
  lastSeenAt: string;
}

export default function App() {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** ⭐ 換對話／開新對話 → 換 key 令 ChatPage remount（重新讀 `sessionId`） */
  const [chatKey, setChatKey] = useState(0);
  const [sessions, setSessions] = useState<DrawerSession[]>([]);
  const loggedIn = auth.status === 'user';
  /** ⭐ UI v2 步 8：dock 上有冇常駐 mini bar */
  const { track } = usePlayer();
  const isPublicPath = location.pathname === '/login' || location.pathname.startsWith('/s/');

  useEffect(() => {
    /**
     * `--app-vh` ＝ **可視區真實高度**（`visualViewport` 最準；唔支援就退 `innerHeight`）
     *   → `.shell` 用佢做高度 → 唔會再出現「shell 短過螢幕」嘅白邊，亦唔會整頁捲。
     */
    const setVh = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      if (h > 0) document.documentElement.style.setProperty('--app-vh', `${Math.round(h)}px`);
    };
    setVh();
    window.visualViewport?.addEventListener('resize', setVh);
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    return () => {
      window.visualViewport?.removeEventListener('resize', setVh);
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  /**
   * ⭐ **URL 恢復碼（`?device=<code>`）** —— 清咗瀏覽器資料都救得返個人 library。
   *   `https://cosingery.com/?device=<你嘅恢復碼>` → 驗證真有歌才採用。
   */
  useEffect(() => {
    void (async () => {
      const result = await adoptDeviceFromUrl();
      if (result === 'adopted') {
        window.location.replace('/songs');
        return;
      }
      if (result === 'empty' || result === 'offline') {
        try {
          sessionStorage.setItem('cosingery.restoreFailed', result);
        } catch {
          /* 私隱模式 */
        }
        window.location.replace('/me');
      }
    })();
  }, []);

  /** 抽屜用嘅「之前嘅對話」清單（`GET /api/chat/sessions`；開抽屜時最update） */
  useEffect(() => {
    if (!loggedIn) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`/api/chat/sessions?deviceId=${encodeURIComponent(getDeviceId())}`);
        const data = (await res.json().catch(() => ({}))) as { sessions?: SessionRow[] };
        if (!alive) return;
        setSessions(
          (data.sessions ?? []).map((s) => ({
            id: s.sessionId,
            name: s.title?.trim() || s.preview || t('history_untitled'),
            meta: t('history_songs', { n: s.songs }),
          })),
        );
      } catch {
        /* 照實：攞唔到清單就唔顯示（唔准假成功） */
      }
    })();
    return () => {
      alive = false;
    };
  }, [loggedIn, drawerOpen, t]);

  /** 抽屜導航：'new' ＝ 開新對話；其餘 ＝ 路徑 */
  const onNavigate = (to: string) => {
    if (to === 'new') {
      startNewSession();
      setChatKey((k) => k + 1);
      navigate('/chat');
      return;
    }
    navigate(to);
  };

  const onOpenSession = (id: string) => {
    openSessionId(id);
    setChatKey((k) => k + 1);
    navigate('/chat');
  };

  const showChrome = loggedIn && !isPublicPath;

  return (
    <div className="shell">
      <div className="shell__frame">
        {showChrome ? (
          <TopBar
            variant="brand"
            title={t('app_name')}
            menuLabel={t('menu_open_aria')}
            onMenu={() => setDrawerOpen(true)}
          />
        ) : null}

        <main className="shell__main">
          <Routes>
            {/* ⭐ 0017：登入頁（未登入嘅唯一入口） */}
            <Route path="/login" element={loggedIn ? <Navigate to="/chat" replace /> : <LoginPage />} />
            {/* ⭐ 公開分享頁（`decisions/031`）—— 收件人直接開（唔需要登入） */}
            <Route path="/s/:token" element={<SharePage />} />
            {loggedIn ? (
              <>
                <Route path="/" element={<Navigate to="/chat" replace />} />
                <Route path="/chat" element={<ChatPage key={chatKey} />} />
                <Route path="/songs" element={<SongsPage />} />
                <Route path="/player" element={<PlayerPage />} />
                <Route path="/me" element={<MePage />} />
                {/* ⭐ UI v2 步 9（設計 S7）：設定頁 */}
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/chat" replace />} />
              </>
            ) : (
              /**
               * ⚠️ 未登入：`loading`（仲未問完 server）→ 唔跳（避免閃一下登入頁）；
               *   確認未登入 → 一律去 `/login`。
               */
              <Route
                path="*"
                element={auth.status === 'loading' || isPublicPath ? null : <Navigate to="/login" replace />}
              />
            )}
          </Routes>
        </main>

        {/* ⭐ UI v2 步 8：常駐 **mini bar**（設計 C10）＋ ⭐ `decisions/053`：底部 **tab bar**（3 格） */}
        {showChrome ? (
          <div className="shell__dock">
            {track ? <MiniBar onOpen={() => navigate('/player')} /> : null}
            <nav className="tabbar" aria-label={t('app_name')}>
              {TABS.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  className={({ isActive }) => 'tabbar__item' + (isActive ? ' tabbar__item--active' : '')}
                >
                  <tab.Icon size="1.375rem" />
                  <span>{t(tab.key)}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        ) : null}
      </div>

      {/* ⭐ `decisions/053`：底部 tab bar 回歸（3 格）；⋯ 抽屜保留（設定類） */}
      {showChrome ? (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          route={location.pathname}
          onNavigate={onNavigate}
          sessions={sessions}
          onOpenSession={onOpenSession}
        />
      ) : null}
    </div>
  );
}

